import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Clock, Users, UserCheck, UserX, LogIn, LogOut, 
  Calendar, Download, RefreshCw, ChevronLeft, ChevronRight, Edit, Save, X, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimeClockAdmin = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in_time: '', clock_out_time: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, [selectedDate]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeclock/dashboard?date=${selectedDate}`);
      setDashboardData(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    }
    setLoading(false);
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const exportToCSV = () => {
    if (!dashboardData) return;
    
    const allEmployees = [
      ...dashboardData.clocked_out.map(e => ({ ...e, status: 'Completed' })),
      ...dashboardData.clocked_in.map(e => ({ ...e, status: 'Working' })),
      ...dashboardData.not_clocked_in.map(e => ({ ...e, status: 'Not Clocked In' }))
    ];
    
    const headers = ['Name', 'Username', 'Status', 'Time In', 'Time Out', 'Method In', 'Method Out'];
    const rows = allEmployees.map(emp => [
      emp.full_name,
      emp.username,
      emp.status,
      emp.clock_in_time || '-',
      emp.clock_out_time || '-',
      emp.clock_in_method || '-',
      emp.clock_out_method || '-'
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${selectedDate}.csv`;
    a.click();
    toast.success('Exported successfully');
  };

  const exportToExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/timeclock/export/excel?start_date=${selectedDate}&end_date=${selectedDate}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${selectedDate}.xlsx`;
      a.click();
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  // Open edit dialog for an employee
  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setEditForm({
      clock_in_time: employee.clock_in_time || '',
      clock_out_time: employee.clock_out_time || ''
    });
    setShowEditDialog(true);
  };

  // Save edited time
  const saveEditedTime = async () => {
    if (!editingEmployee) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/timeclock/dashboard/edit`, {
        user_id: editingEmployee.user_id,
        date: selectedDate,
        clock_in_time: editForm.clock_in_time || null,
        clock_out_time: editForm.clock_out_time || null
      });
      
      toast.success(`Time updated for ${editingEmployee.full_name}`);
      setShowEditDialog(false);
      setEditingEmployee(null);
      fetchDashboard(); // Refresh data
    } catch (error) {
      console.error('Error saving time:', error);
      toast.error(error.response?.data?.detail || 'Failed to save time');
    } finally {
      setSaving(false);
    }
  };

  // Sync/Repair timesheets
  const [syncing, setSyncing] = useState(false);
  const syncTimesheets = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/timeclock/repair-timesheets`);
      const data = res.data;
      if (data.details && data.details.length > 0) {
        toast.success(`Synced ${data.details.length} items to Timesheet Management`);
      } else {
        toast.info('All timesheets are already in sync');
      }
      fetchDashboard();
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync timesheets');
    } finally {
      setSyncing(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-red-500">Admin access required</p>
      </div>
    );
  }

  const summary = dashboardData?.summary || { total_employees: 0, clocked_in: 0, clocked_out: 0, not_clocked_in: 0 };

  const getFilteredEmployees = () => {
    if (!dashboardData) return [];
    switch (activeTab) {
      case 'in': return dashboardData.clocked_in;
      case 'out': return dashboardData.clocked_out;
      case 'absent': return dashboardData.not_clocked_in;
      default: return [
        ...dashboardData.clocked_in.map(e => ({ ...e, _status: 'in' })),
        ...dashboardData.clocked_out.map(e => ({ ...e, _status: 'out' })),
        ...dashboardData.not_clocked_in.map(e => ({ ...e, _status: 'absent' }))
      ];
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Attendance Dashboard</h1>
            <p className="text-slate-500">Real-time employee time tracking</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
          </div>
        </div>

        {/* Date Navigator */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <span className="font-semibold text-lg">{formatDate(selectedDate)}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigateDate(1)}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
                {selectedDate !== new Date().toISOString().split('T')[0] && (
                  <Button variant="outline" size="sm" onClick={goToToday}>
                    Today
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={syncTimesheets}
                  disabled={syncing}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="sync-timesheets-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Sync to Timesheet
                </Button>
                <Button variant="outline" size="sm" onClick={fetchDashboard}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-700 text-white cursor-pointer hover:bg-slate-600" onClick={() => setActiveTab('all')}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <Users className="h-8 w-8 opacity-80" />
                    <div className="text-right">
                      <p className="text-3xl font-bold">{summary.total_employees}</p>
                      <p className="text-sm opacity-80">Total Staff</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-green-500 text-white cursor-pointer hover:bg-green-600" onClick={() => setActiveTab('in')}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <UserCheck className="h-8 w-8 opacity-80" />
                    <div className="text-right">
                      <p className="text-3xl font-bold">{summary.clocked_in}</p>
                      <p className="text-sm opacity-80">Working</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-500 text-white cursor-pointer hover:bg-blue-600" onClick={() => setActiveTab('out')}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <LogOut className="h-8 w-8 opacity-80" />
                    <div className="text-right">
                      <p className="text-3xl font-bold">{summary.clocked_out}</p>
                      <p className="text-sm opacity-80">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-amber-500 text-white cursor-pointer hover:bg-amber-600" onClick={() => setActiveTab('absent')}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <UserX className="h-8 w-8 opacity-80" />
                    <div className="text-right">
                      <p className="text-3xl font-bold">{summary.not_clocked_in}</p>
                      <p className="text-sm opacity-80">Not Clocked In</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 border-b pb-2">
              {[
                { key: 'all', label: 'All Employees' },
                { key: 'in', label: 'Currently Working' },
                { key: 'out', label: 'Completed' },
                { key: 'absent', label: 'Not Clocked In' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                    activeTab === tab.key 
                      ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Employee List */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="attendance-table">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Employee</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-600">Status</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-600">Time In</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-600">Time Out</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-600">Method</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredEmployees().map((emp, idx) => {
                        const status = emp._status || (emp.clock_out_time ? 'out' : (emp.clock_in_time ? 'in' : 'absent'));
                        return (
                          <tr key={emp.user_id || idx} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-800">{emp.full_name}</p>
                                <p className="text-sm text-slate-500">@{emp.username}</p>
                              </div>
                            </td>
                            <td className="text-center py-3 px-4">
                              {status === 'in' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                  Working
                                </span>
                              )}
                              {status === 'out' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  <LogOut className="h-3 w-3" />
                                  Completed
                                </span>
                              )}
                              {status === 'absent' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                  <UserX className="h-3 w-3" />
                                  Absent
                                </span>
                              )}
                            </td>
                            <td className="text-center py-3 px-4">
                              {emp.clock_in_time ? (
                                <span className="font-mono text-green-600">{emp.clock_in_time}</span>
                              ) : (
                                <span className="text-slate-400">--:--</span>
                              )}
                            </td>
                            <td className="text-center py-3 px-4">
                              {emp.clock_out_time ? (
                                <span className="font-mono text-red-600">{emp.clock_out_time}</span>
                              ) : (
                                <span className="text-slate-400">--:--</span>
                              )}
                            </td>
                            <td className="text-center py-3 px-4">
                              {emp.clock_in_method && (
                                <span className="text-xs text-slate-500">
                                  {emp.clock_in_method === 'fingerprint' ? '👆' : '✋'}
                                  {emp.clock_out_method && ` / ${emp.clock_out_method === 'fingerprint' ? '👆' : '✋'}`}
                                </span>
                              )}
                            </td>
                            <td className="text-center py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(emp)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                data-testid={`edit-time-${emp.user_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {getFilteredEmployees().length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-slate-500">
                            No employees in this category
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Edit Time Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Edit Time - {editingEmployee?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Date: <span className="font-medium">{selectedDate}</span>
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-clock-in">Time In</Label>
                <Input
                  id="edit-clock-in"
                  type="time"
                  value={editForm.clock_in_time}
                  onChange={(e) => setEditForm({ ...editForm, clock_in_time: e.target.value })}
                  className="font-mono"
                  data-testid="edit-clock-in-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-clock-out">Time Out</Label>
                <Input
                  id="edit-clock-out"
                  type="time"
                  value={editForm.clock_out_time}
                  onChange={(e) => setEditForm({ ...editForm, clock_out_time: e.target.value })}
                  className="font-mono"
                  data-testid="edit-clock-out-input"
                />
              </div>
            </div>
            
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              ⚠️ Changes will sync to both Dashboard and Timesheet Management
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={saving}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              onClick={saveEditedTime}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-edit-time-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeClockAdmin;
